<template lang='pug'>
  div
    v-toolbar(dense)
      v-col(cols='8' no-gutters v-if='dashboard')
        div(@click="toggleEdit").clickable.text-body-2.text-truncate {{dashboard.name}}
        div(@click="toggleEdit").clickable.text-caption.grey--text.text--darken-1.text-truncate {{dashboard.description}}&nbsp;
          display-tags(v-model='dashboard.tags' @tag="toggleEdit")
      v-spacer
      v-tooltip(bottom)
        template(v-slot:activator='{on}')
          v-btn.tight-button(v-on='on' icon @click='toggleEdit')
            v-icon settings
        span Dashboard Settings
      bionic-metrics-run-dialog(v-if='hasRunDialog' :dashboard='dashboard')
    bionic-metrics-page-loading(v-if='loading')
    v-container(fluid v-if='!loading && dashboard').pb-1
      v-card.mb-2(v-if='editing')
        v-tabs(v-model='tab')
          v-tab(key='details') Details
          v-tab(key='configuration') Configuration
          v-tab(key='suites') Suites
          v-tab(key='runConfigurations') Run Configurations
          v-tab(key='notifications') Notifications
        v-tabs-items(v-model='tab')
          v-tab-item(key='details')
            v-card(flat tile)
              v-card-text
                v-text-field(label='Dashboard Name' @input='triggerSave' v-model='dashboard.name' type='text' required)
                v-textarea(outlined label='Description' @input='triggerSave' v-model='dashboard.description' type='text' auto-grow rows='3')
                tags(v-model='dashboard.tags' @input='triggerSave')
                v-checkbox.mb-5(
                  @change='triggerSave'
                  v-model='dashboard.listView'
                  label='List View'
                  persistent-hint
                  hint='Enable list view instead of tile view')
          v-tab-item(key='configuration')
            v-card(flat tile)
              v-card-text
                v-checkbox.mb-5(
                  @change='triggerSave'
                  v-model='dashboard.forceOverride'
                  label='Override'
                  persistent-hint
                  hint='Override the Test Suite Base URL and Inputs')
                v-text-field(
                  v-if='dashboard.forceOverride'
                  label='Override Base URL'
                  @input='triggerSave'
                  placeholder='Base URL prefix (e.g. https://example.com)'
                  v-model='dashboard.baseUrl'
                  type='text')
                bionic-metrics-user-agent(v-if='dashboard.forceOverride' v-model='dashboard.userAgent' @input='triggerSave')
                div(v-if='dashboard.forceOverride')
                  | Override Inputs
                  bionic-metrics-monaco-simple-editor.inline-editor(
                    ref='editor'
                    type='input'
                    git-name='dashboardInputs.json'
                    v-model='dashboard.json'
                    @input='triggerSave'
                    mode='json')
          v-tab-item(key='suites')
            v-card(flat tile)
              v-card-text
                bionic-metrics-suite-list(:dashboard='dashboard' v-model='dashboard.testSuites' @input='triggerSave' @removed='cleanupForRemovedSuite')
          v-tab-item(key='runConfigurations')
            v-card(flat tile)
              bionic-metrics-run-configurations(v-model='dashboard.runConfigurations' :dashboard='dashboard' @input='triggerSave')
          v-tab-item(key='notifications')
            v-card(flat tile)
              bionic-metrics-notifications(v-model='dashboard.notifications' :dashboard='dashboard' @input='triggerSave')
      v-row(dense)
        v-col(cols='6' sm='6' md='3' lg='2' xl='2')
          v-tooltip(bottom)
            template(v-slot:activator='{on}')
              v-card(v-on='on').full-width
                v-card-text.text-center
                  .text-h2.green--text.accent-4 {{dashboard.passing || 0}}
                  .text-subtitle-1.grey--text Passing
            div
              div Passing: {{dashboard.passing || 0}}
              div No Results: {{dashboard.noResults || 0}}
        v-col(cols='6' sm='6' md='3' lg='2' xl='2')
          v-tooltip(bottom)
            template(v-slot:activator='{on}')
              v-card(v-on='on').full-width
                v-card-text.text-center
                  .text-h2.deep-orange--text.text--darken-3 {{dashboard.failing || 0}}
                  .text-subtitle-1.grey--text Failing
            div
              div Failing: {{dashboard.failing || 0}}
        v-col(cols='12' sm='12' md='6' lg='8' xl='8')
          bionic-metrics-at-a-glance(v-if='dashboard' :dashboardGuid='dashboard.guid')
    bionic-metrics-test-status(
      v-if='dashboard'
      :dashboardGuid='dashboard.guid'
      :listView='dashboard.listView'
      :testSuites='dashboard.testSuites'
      @refresh='refresh'
      @newRunGuid='newRunGuid')
</template>


<script>
import {get} from 'lodash';
import {mapGetters, mapMutations} from 'vuex';
import Dashboard from '@/api/dashboard';
import Notifications from '@/services/notification.service';
import SaveMixin from '@/mixins/save.mixin';
import UtilMixin from '@/mixins/util.mixin';
import BionicMetricsSpinner from '@/components/Spinner';
import BionicMetricsNumber from '@/components/Number';
import BionicMetricsTestStatus from '@/components/TestStatus';
import BionicMetricsAtAGlance from '@/components/AtAGlance';
import TableList from '@/components/TableList';
import BionicMetricsPageLoading from '@/components/PageLoading';
import BionicMetricsRunDialog from '@/components/RunDialog';
import DisplayTags from '@/components/DisplayTags';
import BionicMetricsMonacoSimpleEditor from '@/components/MonacoSimpleEditor';
import Tags from '@/components/Tags';
import BionicMetricsSuiteList from '@/components/SuiteList';
import BionicMetricsRunConfigurations from '@/components/RunConfigurations';
import BionicMetricsNotifications from '@/components/Notifications';
import BionicMetricsUserAgent from '@/components/UserAgent';


export default {
  name: 'Dashboard',
  props: {
    id: {
      type: String,
      default: 'primary'
    }
  },
  mixins: [UtilMixin, SaveMixin],
  components: {
    BionicMetricsUserAgent,
    BionicMetricsNotifications,
    BionicMetricsRunConfigurations,
    BionicMetricsSuiteList,
    Tags,
    BionicMetricsMonacoSimpleEditor,
    DisplayTags,
    BionicMetricsRunDialog,
    BionicMetricsPageLoading,
    TableList,
    BionicMetricsAtAGlance,
    BionicMetricsTestStatus,
    BionicMetricsNumber,
    BionicMetricsSpinner
  },
  async mounted() {
    await this.load();
  },
  computed: {
    saveObject() {
      return this.dashboard;
    },
    hasRunDialog() {
      return this.dashboard && this.dashboard.testSuites && this.dashboard.testSuites.length;
    },
    ...mapGetters({
      user: 'getUser',
      account: 'getAccount',
      gitMode: 'getGitMode',
      gitBranch: 'getGitBranch'
    })
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        const id = this.gitMode ? 'git' : this.id;
        const branch = this.gitMode ? this.gitBranch : null;
        this.dashboard = await Dashboard.summary(id, branch); // if gitMode id is git with branch as query param
        this.updateBreadcrumb();
      } catch (error) {
        const code = get(error, 'response.status');
        console.error(`Problem getting dashboard: ${error.toString()}`);
        if (this.gitMode && code === 404) {
          const yes = await Notifications.confirm('Yes', `Dashboard does not exist for <strong>${this.gitBranch}</strong>. Would you like to create one?`);
          if (yes) return this.$router.push('/?create=true');
          return this.abort();
        }
        await Notifications.errorToast('Error Loading Dashboard', {duration: 30000, retry: this.load});
        this.abort();
      } finally {
        this.loading = false;
      }
    },
    async refresh() {
      try {
        const id = this.gitMode ? 'git' : this.id;
        const branch = this.gitMode ? this.gitBranch : null;
        const dashboard = await Dashboard.summary(id, branch);
        this.dashboard.passing = dashboard.passing;
        this.dashboard.failing = dashboard.failing;
        this.dashboard.noResults = dashboard.noResults;
      } catch (e) {
        console.error(`Error refreshing dashboard: ${e.toString()}`);
      }
    },
    async save(dashboard) {
      try {
        await Dashboard.update(dashboard.guid, dashboard);
      } catch (error) {
        console.error(error);
        Notifications.errorToast(`There was a problem saving <strong>${dashboard.name}</strong>`, {
          duration: 30000,
          retry: () => this.save(dashboard)
        });
      }
    },
    async newRunGuid({testSuiteGuid, runGuid}) {
      const testSuite = this.dashboard.testSuites.find((ts) => ts.guid === testSuiteGuid);
      testSuite.runGuid = runGuid;
    },
    cleanupForRemovedSuite(testSuiteGuid) {
      if (this.dashboard.runConfigurations) {
        for (const rc of this.dashboard.runConfigurations) {
          rc.testSuites = rc.testSuites.filter((ts) => ts.guid !== testSuiteGuid);
        }
        // Remove any that don't have suites
        this.dashboard.runConfigurations = this.dashboard.runConfigurations.filter((rc) => rc.testSuites.length);
      }

      if (this.dashboard.notifications) {
        for (const notif of this.dashboard.notifications) {
          notif.testSuites = notif.testSuites.filter((ts) => ts.guid !== testSuiteGuid);
        }
        // Remove any that don't have suites
        this.dashboard.notifications = this.dashboard.notifications.filter((rc) => rc.testSuites.length);
      }
      this.triggerSave();
    },
    toggleEdit() {
      this.editing = !this.editing;
    },
    updateBreadcrumb() {
      this.setBreadcrumb([{text: 'Dashboards', to: '/'}, {text: this.dashboard.name}]);
    },
    ...mapMutations({
      setBreadcrumb: 'setBreadcrumb'
    })
  },
  watch: {
    'dashboard.name': {
      handler: function () { // eslint-disable-line
        this.updateBreadcrumb();
      },
      deep: true
    }
  },
  data() {
    return {
      tab: 'details',
      loading: false,
      editing: false,
      dashboard: null
    };
  }
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
.inline-editor {
  width: 100%;
  min-height: 20vh;
}
</style>
